import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMisconceptionReport } from '../../services/api';

interface RecentMistake {
  question_id: string;
  learning_objective: string;
  unit: number;
  lesson: number;
  prompt: string;
  given_answer: string;
  correct_answer: string;
  explanation: string;
  date: string;
}

interface MisconceptionData {
  description: string;
  count: number;
  affected_los: string[];
  affected_units: number[];
}

interface MisconceptionReportData {
  total_mistakes: number;
  common_misconceptions: MisconceptionData[];
  recent_mistakes: RecentMistake[];
}

interface MisconceptionReportProps {
  studentId: string;
}

export const MisconceptionReport: React.FC<MisconceptionReportProps> = ({ studentId }) => {
  const [data, setData] = useState<MisconceptionReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    getMisconceptionReport(studentId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) {
    return <div className="misconception-report loading">Loading...</div>;
  }

  if (!data || data.total_mistakes === 0) {
    return (
      <div className="misconception-report empty">
        <h3>Recent Mistakes</h3>
        <p className="no-data">No mistakes to review yet. Keep up the great work!</p>
      </div>
    );
  }

  const hasPatterns = (data.common_misconceptions?.length ?? 0) > 0;
  const hasMistakes = (data.recent_mistakes?.length ?? 0) > 0;

  if (!hasPatterns && !hasMistakes) {
    return (
      <div className="misconception-report empty">
        <h3>Recent Mistakes</h3>
        <p className="no-data">No recent mistakes to review.</p>
      </div>
    );
  }

  return (
    <div className="misconception-report">
      <h3>Recent Mistakes</h3>

      {/* Common Error Patterns */}
      {hasPatterns && (
        <div className="error-patterns-section">
          <h4>Common Patterns</h4>
          <div className="pattern-list">
            {data.common_misconceptions.slice(0, 4).map((m, idx) => (
              <div key={idx} className="pattern-item">
                <span className="pattern-count">{m.count}x</span>
                <span className="pattern-desc">{m.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Mistakes */}
      {hasMistakes && (
        <div className="recent-mistakes-section">
          <h4>Recent</h4>
          <div className="mistakes-list">
            {data.recent_mistakes.slice(0, 5).map((m, idx) => (
              <div key={idx} className="mistake-item">
                <div className="mistake-header">
                  <span className="mistake-lo">{m.learning_objective}</span>
                  <span className="mistake-date">
                    {new Date(m.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
                <p className="mistake-prompt">{m.prompt}</p>
                <div className="mistake-answers">
                  <span className="answer-wrong">You: {m.given_answer}</span>
                  <span className="answer-correct">Answer: {m.correct_answer}</span>
                </div>
                <div className="mistake-actions">
                  {m.explanation && (
                    <button onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}>
                      {expandedIdx === idx ? 'Hide solution' : 'Show solution'}
                    </button>
                  )}
                  <button onClick={() => navigate(`/practice/${m.unit}/${m.lesson}?similar_to=${m.question_id}`)}>
                    Practice similar
                  </button>
                </div>
                {expandedIdx === idx && m.explanation && (
                  <div className="mistake-explanation">{m.explanation}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="total-count">{data.total_mistakes} total mistakes analyzed</p>
    </div>
  );
};

export default MisconceptionReport;
